import { z } from 'zod';
import { LEVELS, ROLES, CATEGORIES, DEFINITION_KINDS, define } from '../types.js';

/**
 * White-label branding configuration.
 */
export const schema = z
  .object({
    active: z
      .boolean()
      .describe(
        "Activate/Deactivate the branding feature for clients at this level. Branded clients will be visible to their customers not as HopDrive, but as the client's own brand."
      )
      .optional(),
    name: z.string().describe("Name to be displayed to the client's customers.").optional(),
    primary_color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .describe('Primary brand color in hex format (e.g., "#0d5298"). Used for core UI elements with emphasis.')
      .nullable()
      .optional()
      .or(z.literal('')),
    secondary_color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .describe('Secondary/accent brand color in hex format. Used for secondary UI elements and highlights.')
      .nullable()
      .optional()
      .or(z.literal('')),
    backdrop_color: z
      .string()
      .regex(/^#[0-9A-Fa-f]{6}$/)
      .describe('Background color for logo area in hex format. Be sure to contrast this to the logo for visibility.')
      .nullable()
      .optional()
      .or(z.literal('')),
    logo_url: z
      .string()
      .url()
      .describe(
        'The logo is a link to a PNG or SVG file to give clients at this level some imagery to go along with their name & colors. Due to limitations, the maximum file size is 0.5MB/500KB. Please also make sure the image dimensions are at least 40px tall as anything lower will be too low of a resolution. Stored in hopdrive-branding-logos S3 bucket.'
      )
      .optional()
      .or(z.literal('')),
    customer_support_phone_number: z
      .string()
      .describe(
        'Customer support phone for HopDrive Lite tier ONLY. Displayed in concierge SMS to consumers. Not applicable to Standard tier.'
      )
      .optional(),
  })
  .strict()
  .describe(
    'The branding module is a way for us to white-label our clients. White-labeling is a way to display our clients in a unique way compared to the HopDrive default branding.'
  );

export type Branding = z.infer<typeof schema>;

export const definition = define({
  key: 'branding',
  schema,
  label: 'Branding',
  description:
    'The branding module is a way for us to white-label our clients. White-labeling is a way to display our clients in a unique way compared to the HopDrive default branding.',
  warning: 'Only available to organizations. Customer branding inherits from parent org.',
  kind: DEFINITION_KINDS.SETTING,
  category: CATEGORIES.BRANDING.key,
  writableAtLevels: [LEVELS.HOPDRIVE, LEVELS.ORGANIZATION, LEVELS.CUSTOMER],
  readableByRoles: [ROLES.ADMIN],
  defaultValue: {
    active: false,
    name: '',
    primary_color: '',
    secondary_color: '',
    backdrop_color: '',
    logo_url: '',
    customer_support_phone_number: '',
  },
});
